import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type VaultRecord, type NeoVerifyResult } from '../api';

/**
 * NeoVerify — check a document against the registry.
 *
 * The web app showed "NeoVerified" badges but could not run a check: nothing
 * called `/vault` (the records with their verification state) or
 * `/neoverify/:id` (the verdict). This patient holds 16 documents across
 * eight categories whose verification state has never been visible here.
 *
 * The verdict is the registry's and is quoted as it comes. A document is
 * authentic only while its digest still matches the one recorded at
 * registration; an id that was printed but never registered comes back
 * UNREGISTERED, and that is shown as plainly as a pass — on paper the two
 * look identical, which is the whole reason to check.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

/**
 * How each state is described, in the registry's terms rather than ours.
 *
 * Every verdict the registry can return is named here. Four were missing —
 * ALTERED, UNCHECKABLE, REVOKED and SUPERSEDED — so they fell through to a
 * neutral grey chip with no explanation. ALTERED means the document no longer
 * matches the digest recorded when it was registered, which is the one finding
 * this whole system exists to surface; it was rendering as quietly as a pass.
 */
const STATE: Record<string, { color: string; label: string; meaning: string }> = {
  VERIFIED: { color: T.success, label: 'Verified', meaning: 'Registered by the issuer and unchanged since.' },
  AUTHENTIC: { color: T.success, label: 'Authentic', meaning: 'Still matches the digest recorded at registration.' },
  INSURER_CONFIRMED: { color: T.info, label: 'Confirmed by insurer', meaning: 'Your insurer confirmed this record.' },
  SELF_ENTERED: { color: T.textSec, label: 'Entered by you', meaning: 'Your own entry — no provider has registered it, so nothing here vouches for it.' },
  ALTERED: {
    color: T.danger,
    label: 'Altered',
    meaning: 'This document no longer matches the digest recorded when it was registered. Do not rely on it — ask the issuer for a fresh copy.',
  },
  REVOKED: {
    color: T.danger,
    label: 'Withdrawn by issuer',
    meaning: 'The issuer withdrew this document. It should not be relied on.',
  },
  SUPERSEDED: {
    color: T.warning,
    label: 'Replaced by issuer',
    meaning: 'The issuer replaced this with a newer document. Ask them for the current one.',
  },
  UNCHECKABLE: {
    color: T.warning,
    label: 'Could not be checked',
    meaning: 'The registry entry exists, but the record behind it could not be read to re-check its digest. This is not a finding either way.',
  },
  UNREGISTERED: { color: T.danger, label: 'Not registered', meaning: 'No registry record. It may still be genuine paper, but nothing here backs it.' },
};

/**
 * An unrecognised verdict is reported as unrecognised.
 *
 * Deliberately NOT folded into any of the states above: a word this build does
 * not know is not a pass and not a failure, and saying so is the only honest
 * thing a verification screen can do with it.
 */
function tone(state: string) {
  const key = (state || '').toUpperCase();
  return STATE[key] ?? {
    color: T.textSec,
    label: key || 'Unknown',
    meaning: 'This app does not recognise that verdict, so it is neither passing nor failing the document. Quote the id to the issuer.',
  };
}

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NeoVerifyCheck() {
  const [records, setRecords] = useState<VaultRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [category, setCategory] = useState('All');

  const [manualId, setManualId] = useState('');
  const [checking, setChecking] = useState('');
  const [result, setResult] = useState<NeoVerifyResult | null>(null);
  const [checkError, setCheckError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setRecords(await patientApi.vault() ?? []);
    } catch (e: any) {
      setLoadError(e?.body?.message || 'Your documents could not be loaded — check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // The QR carries the id anyone else can check, not the document itself.
  useEffect(() => {
    let alive = true;
    if (!result?.neoVerifyId) { setQrDataUrl(''); return; }
    import('qrcode')
      .then((QRCode) => QRCode.toDataURL(result.neoVerifyId, { margin: 1, width: 150 }))
      .then((url: string) => { if (alive) setQrDataUrl(url); })
      .catch(() => { if (alive) setQrDataUrl(''); });
    return () => { alive = false; };
  }, [result]);

  const check = async (neoVerifyId: string) => {
    setChecking(neoVerifyId);
    setCheckError('');
    setResult(null);
    try {
      setResult(await patientApi.neoVerify(neoVerifyId));
    } catch (e: any) {
      setCheckError(e?.body?.message || 'That id could not be checked right now.');
    } finally {
      setChecking('');
    }
  };

  const categories = ['All', ...Array.from(new Set(records.map((r) => r.category))).sort()];
  const shown = category === 'All' ? records : records.filter((r) => r.category === category);

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>NeoVerify</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          Check a document against the registry. The verdict is the registry&rsquo;s, not this app&rsquo;s.
        </p>
      </div>

      {/* Check any id — someone else's copy, or one read off paper */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Check an id</div>
        <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
          {/* "Anyone can check one" was not true: every verify endpoint on this
              platform is behind a sign-in and scoped to a role. A person with
              no NeoHealthCard account has nowhere to take the id. */}
          Every NeoHealthCard document carries a NeoVerify id. You can check any id here, and so can
          any hospital, lab or clinic on NeoHealthCard from their own panel.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <input
            aria-label="NeoVerify id"
            value={manualId}
            onChange={(e) => setManualId(e.target.value)}
            placeholder="NV-…"
            style={{
              flex: 1, minWidth: 180, padding: '10px 12px', border: `1px solid ${T.border}`,
              borderRadius: 10, fontSize: 13, fontFamily: 'monospace', color: T.text,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          <button
            onClick={() => check(manualId)}
            disabled={!manualId.trim() || checking !== ''}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              cursor: !manualId.trim() ? 'default' : 'pointer', fontSize: 12, fontWeight: 700,
              color: T.white, background: T.teal, fontFamily: T.font,
              opacity: !manualId.trim() || checking !== '' ? 0.55 : 1,
            }}
          >
            {checking ? 'Checking…' : 'Check'}
          </button>
        </div>
        {checkError && (
          <div style={{ fontSize: 11.5, color: T.danger, marginTop: 10 }}>{checkError}</div>
        )}
      </div>

      {/* The verdict */}
      {result && (
        <div style={{ ...card, borderColor: `${tone(result.result).color}55` }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <span style={{
                fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: T.white,
                background: tone(result.result).color, borderRadius: 6, padding: '3px 8px',
                textTransform: 'uppercase',
              }}>
                {tone(result.result).label}
              </span>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginTop: 10 }}>
                {result.documentTitle || 'No document under this id'}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 11.5, color: T.textSec, marginTop: 4 }}>
                {result.neoVerifyId}
              </div>

              {result.note && (
                <div style={{ fontSize: 11.5, color: T.text, marginTop: 10, lineHeight: 1.55 }}>{result.note}</div>
              )}

              {result.issuerName && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[
                    ['Issued by', [result.issuerName, result.issuerNhcId].filter(Boolean).join(' · ')],
                    ['Document', [result.documentType?.replace(/_/g, ' ').toLowerCase(), result.documentNumber].filter(Boolean).join(' · ')],
                    ['Issued', fmtDate(result.issuedAt)],
                    ['Registered', fmtDate(result.registeredAt)],
                    ['Digest', [result.hashAlgorithm, result.registry].filter(Boolean).join(' · ')],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5 }}>
                      <span style={{ color: T.textSec }}>{k}</span>
                      <span style={{ color: T.text, textAlign: 'right' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {qrDataUrl && (
              <div style={{ textAlign: 'center' }}>
                <img src={qrDataUrl} alt={`QR code for ${result.neoVerifyId}`} width={132} height={132} style={{ borderRadius: 10 }} />
                <div style={{ fontSize: 9.5, color: T.textTer, marginTop: 4, maxWidth: 132, lineHeight: 1.4 }}>
                  {/* It carries the id, not a link — there is no public verify
                      page, so a scan hands the id to whoever has a panel to
                      check it in. Saying "anyone" invited a stranger to scan
                      it and find nothing. */}
                  Carries the id. A NeoHealthCard provider scans it to run this same check.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* The patient's own documents and where each stands */}
      <div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.font, marginTop: 4 }}>
        Your documents
      </div>

      {loadError && (
        <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading your documents…</div>
      ) : records.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>
          No documents yet. Anything a provider issues to you appears here with its registry state.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                style={{
                  padding: '6px 13px', borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontSize: 11, fontWeight: 700, fontFamily: T.font,
                  color: category === c ? T.white : T.textSec,
                  background: category === c ? T.teal : T.white,
                  border: `1px solid ${category === c ? T.teal : T.border}`,
                }}
              >
                {c}
              </button>
            ))}
          </div>

          {shown.map((r) => {
            const st = tone(r.verification);
            return (
              <div key={`${r.source}-${r.id}`} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{r.title}</div>
                    <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 2 }}>
                      {[r.provider, r.documentNumber, fmtDate(r.date)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 9.5, fontWeight: 700, color: T.white, whiteSpace: 'nowrap',
                    background: st.color, borderRadius: 8, padding: '3px 8px',
                  }}>
                    {st.label}
                  </span>
                </div>

                {st.meaning && (
                  <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 8, lineHeight: 1.5 }}>{st.meaning}</div>
                )}

                {r.neoVerifyId && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 10.5, color: T.textTer }}>{r.neoVerifyId}</span>
                    <button
                      onClick={() => { setManualId(r.neoVerifyId!); void check(r.neoVerifyId!); }}
                      disabled={checking === r.neoVerifyId}
                      style={{
                        padding: '7px 14px', borderRadius: 10, border: `1px solid ${T.teal}40`,
                        background: T.white, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                        color: T.teal, fontFamily: T.font,
                      }}
                    >
                      {checking === r.neoVerifyId ? 'Checking…' : 'Check with the registry'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
